import React from "react";
import ProfileBehaviourCard from "./ProfileBehaviourCard";
import AccountProfileHeaderDataCard from "./AccountProfileHeaderDataCard";
import ProfileCard from "./ProfileCard";
import ProfileInfo from "./ProfileInfo";
import Solutions from "./Solutions";
import {
  ArrowUp,
  ArrowDown,
  Store,
  Activity,
  Cpu,
  Flag,
  AlertTriangle,
  PiggyBank,
  Target,
  TrendingUp,
  Info,
} from "lucide-react";
import {
  formatPeopleCountShort,
  formatRevenueShort,
  ACCOUNT_REVENUE_UNAVAILABLE,
  isNewFormatAccountProfile,
} from "@/shared/utils/helperFunctions";
import Loader from "@/shared/components/ui/Loaders/Loader";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import { getDisplayLocation } from "@/shared/utils/accountConfig";

const getStatusVariant = (value) => {
  if (!value) return "neutral";
  const num = Number(value);
  if (Number.isFinite(num) && typeof value !== "boolean") {
    if (num >= 75) return "success";
    if (num >= 50) return "warning";
    return "danger";
  }
  const val = String(value).toLowerCase();
  if (["success", "high", "growing", "grown", "shopping", "active"].includes(val)) return "success";
  if (["warning", "medium", "med", "exploring", "stagnant", "warm"].includes(val)) return "warning";
  if (["danger", "low", "window shopping", "declining", "cold"].includes(val)) return "danger";
  return "neutral";
};

const AccountProfile = ({
  profile,
  accountRevenue,
  isRevenuePending = false,
}) => {
  // Determine if this is the new data format or old data format
  const isNewFormat = isNewFormatAccountProfile(profile);
  const isRevenueLoading = isRevenuePending && !accountRevenue;

  // Placeholder row while account research is still running in the background
  const isResearchPending =
    !isNewFormat &&
    !profile?.profile_summary &&
    !(profile?.key_challenges?.length > 0);

  if (isResearchPending) {
    return (
      <div className="flex h-full min-h-[300px] w-full items-center justify-center">
        <Loader text="Account research is in progress. This page will be ready shortly..." />
      </div>
    );
  }

  // Safe getters for new/old object mappings
  const revenueVal = accountRevenue
    ? formatRevenueShort(accountRevenue.value)
    : ACCOUNT_REVENUE_UNAVAILABLE;
  const rawYoy = accountRevenue?.yoy_change ?? "";
  let revenueChange = "";
  if (rawYoy !== "" && rawYoy != null) {
    const numericYoy = parseFloat(String(rawYoy).replace(/[^\d.-]/g, ""));
    if (Number.isFinite(numericYoy) && Math.abs(numericYoy) > 0) {
      let str = String(rawYoy).trim().replace(/\s*YoY\s*/gi, "").trim();
      if (!str.includes("%") && /\d/.test(str)) {
        str = `${str}%`;
      }
      if (str) {
        revenueChange = `${str} YoY`;
      }
    }
  }

  const isNegativeYoy = typeof rawYoy === "number"
    ? rawYoy < 0
    : /-|decrease|down|drop/i.test(String(rawYoy));

  const fiscalYear = accountRevenue?.fiscal_year || "";
  const revenueSourceText = String(accountRevenue?.source || "").trim();
  const revenueSourceUrl = String(accountRevenue?.source_url || "").trim();
  const revenueSourceQuote = String(accountRevenue?.source_quote || "").trim();
  const tooltipContent = accountRevenue
    ? `Source: ${revenueSourceText}\nURL: ${revenueSourceUrl}\nQuote: ${revenueSourceQuote}`
    : "";
  const confidenceLabel = accountRevenue
    ? `${accountRevenue.confidence.toUpperCase()} CONFIDENCE`
    : "";
  const confidenceClassName = accountRevenue?.confidence === "high"
    ? "bg-success-50 text-success-700 dark:bg-emerald-950/40 dark:text-emerald-300"
    : "bg-warning-50 text-warning-700 dark:bg-amber-950/40 dark:text-amber-300";

  const hasHeadcountResearch = profile?.headcount != null;
  const headcountRegion = getDisplayLocation(profile?.headcount?.region);
  const hasKnownHeadcountRegion =
    !hasHeadcountResearch || headcountRegion !== "Not Identified";
  const headcountReg =
    hasHeadcountResearch && hasKnownHeadcountRegion ? headcountRegion : "";
  const headcountVal = formatPeopleCountShort(
    hasKnownHeadcountRegion
      ? profile?.headcount?.value || profile?.company_size
      : null,
  );
  const headcountPer = profile?.headcount?.period || "";
  const headcountScope = profile?.headcount?.scope || "";
  const headcountSubtext = hasKnownHeadcountRegion
    ? [headcountPer, headcountScope]
        .map((s) => String(s).trim())
        .filter(Boolean)
        .join(", ")
    : "";

  const buyingBehaviorDesc = typeof profile?.buying_behaviour === "string"
    ? profile.buying_behaviour
    : (profile?.buying_behavior?.behavior || "Window Shopping");

  const financialHealthDesc = isNewFormat && profile?.business_health?.tag
    ? profile.business_health.tag
    : (typeof profile?.financial_health === "string"
      ? profile.financial_health
      : (profile?.financial_health?.health || "Growing"));

  const financialHealthReason = isNewFormat && profile?.business_health?.summary
    ? profile.business_health.summary
    : profile?.financial_health?.reason;

  const matchScoreDesc = typeof profile?.match_score === "string"
    ? profile.match_score
    : (profile?.match_score?.score !== undefined ? String(profile.match_score.score) : "Unknown");

  const matchScoreReason = profile?.match_score?.reason || "";

  const prioritiesList = profile?.top_priorities || profile?.key_priorities || [];
  const challengesList = profile?.top_challenges || profile?.key_challenges || [];
  const techStack = Array.isArray(profile?.tech_stack) ? profile.tech_stack : [];

  return (
    <div className="w-full overflow-auto flex flex-col font-inter gap-5">
      <div className="grid grid-cols-2 w-full gap-6">
        {/* yoy revenue growth + headcount */}
        <div className="flex w-full gap-6">
          <AccountProfileHeaderDataCard>
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between w-full">
                <h6 className="text-gray-500 dark:text-gray-400 text-xs font-semibold tracking-wider uppercase">
                  {fiscalYear ? `${fiscalYear} REVENUE` : "REVENUE"}
                </h6>
                <div className="flex items-center gap-2">
                  {confidenceLabel && (
                    <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-semibold tracking-wide ${confidenceClassName}`}>
                      {confidenceLabel}
                    </span>
                  )}
                  {tooltipContent && (
                    <CustomCommonTooltip content={tooltipContent} placement="top">
                      <a
                        href={revenueSourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-gray-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Info size={15} />
                      </a>
                    </CustomCommonTooltip>
                  )}
                </div>
              </div>
              {isRevenueLoading ? (
                <div
                  className="flex h-10 items-center gap-2 text-sm font-medium text-gray-400"
                  role="status"
                >
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
                  Loading revenue...
                </div>
              ) : (
                <h2 className="text-2xl font-semibold leading-8 text-gray-900 dark:text-gray-100">
                  {revenueVal}
                </h2>
              )}
              {!isRevenueLoading && revenueChange && (
                <span
                  className={`flex gap-1 text-xs font-medium items-center ${isNegativeYoy ? "text-error-500 dark:text-red-400" : "text-success-700 dark:text-emerald-400"
                    }`}
                >
                  {isNegativeYoy ? (
                    <ArrowDown size={13} strokeWidth={2} />
                  ) : (
                    <ArrowUp size={13} strokeWidth={2} />
                  )}
                  {revenueChange}
                </span>
              )}
            </div>
          </AccountProfileHeaderDataCard>

          {/* headcount */}
          <AccountProfileHeaderDataCard>
            <div className="flex flex-col gap-2.5">
              <h6 className="text-gray-500 dark:text-gray-400 text-xs font-semibold tracking-wider uppercase">
                {headcountReg ? `${headcountReg} Headcount` : "Headcount"}
              </h6>
              <h2 className="text-2xl font-semibold leading-8 text-gray-900 dark:text-gray-100">
                {headcountVal}
              </h2>
              {headcountSubtext && (
                <span
                  className="text-xs font-medium text-gray-500 dark:text-gray-400 line-clamp-1 break-words"
                  title={headcountSubtext}
                >
                  {headcountSubtext}
                </span>
              )}
            </div>
          </AccountProfileHeaderDataCard>

        </div>

        {/* finance and buying behaviour */}
        <AccountProfileHeaderDataCard className="flex items-center justify-between gap-4">
          <ProfileBehaviourCard
            title={"Buying Behaviour"}
            desc={buyingBehaviorDesc}
            variant={getStatusVariant(buyingBehaviorDesc)}
            reason={profile?.buying_behavior?.reason}
            firstCard={true}
          />
          <ProfileBehaviourCard
            title={"Financial Health"}
            variant={getStatusVariant(financialHealthDesc)}
            desc={financialHealthDesc}
            reason={financialHealthReason}
          />
          <ProfileBehaviourCard
            title={"Match Score"}
            variant={getStatusVariant(matchScoreDesc)}
            desc={matchScoreDesc}
            reason={matchScoreReason}
            lastcard={true}
          />
        </AccountProfileHeaderDataCard>
      </div>

      {isNewFormat ? (
        <>
          {/* Grid of Profile Cards */}
          {/* Row 1: What They Sell & Business Health */}
          <div className="grid grid-cols-2 gap-6 w-full mt-1">
            <ProfileCard
              title="What They Sell"
              icon={Store}
              variant="gray"
              type="text"
              data={
                (typeof profile?.what_they_sell === "string" &&
                  profile.what_they_sell) ||
                (typeof profile?.products_and_services_breakdown === "string" &&
                  profile.products_and_services_breakdown) ||
                ""
              }
            />
            <ProfileCard
              title="Business Health"
              icon={Activity}
              variant="gray"
              type="text"
              badge={profile?.business_health?.tag || profile?.financial_health?.health}
              data={
                (typeof profile?.business_health === "string" &&
                  profile.business_health) ||
                profile?.business_health?.summary ||
                profile?.financial_health?.reason ||
                ""
              }
            />
          </div>

          {/* Row 2: Top Priorities & Top Challenges */}
          <div className="grid grid-cols-2 gap-6 w-full mt-1">
            <ProfileCard
              title={
                prioritiesList.length
                  ? `Top ${prioritiesList.length} Priorities`
                  : "Top Priorities"
              }
              icon={Flag}
              variant="green"
              type="rich-bullets"
              data={prioritiesList}
            />
            <ProfileCard
              title={
                challengesList.length
                  ? `Top ${challengesList.length} Challenges`
                  : "Top Challenges"
              }
              icon={AlertTriangle}
              variant="red"
              type="rich-bullets"
              data={challengesList}
            />
          </div>

          {/* Row 3: 4 Columns (Investments, Signals, Themes, Competition) */}
          <div className="grid grid-cols-4 gap-6 w-full mt-1">
            <ProfileCard
              title="Recent Investments"
              icon={PiggyBank}
              variant="gray"
              type="bullets"
              data={profile?.recent_investments}
            />
            <ProfileCard
              title="Top Buying Signals"
              icon={Target}
              variant="gray"
              type="bullets"
              badge={profile?.top_buying_signals?.tag}
              data={
                Array.isArray(profile?.top_buying_signals)
                  ? profile.top_buying_signals
                  : profile?.top_buying_signals?.signals || []
              }
            />
            <ProfileCard
              title="Tech Stack"
              icon={Cpu}
              variant="gray"
              type="rich-bullets"
              data={techStack}
            />
            <ProfileCard
              title="Competitive Pressure"
              icon={TrendingUp}
              variant="gray"
              type="rich-bullets"
              badge={profile?.competitive_pressure?.tag}
              description={
                typeof profile?.competitive_pressure === "string"
                  ? profile.competitive_pressure
                  : profile?.competitive_pressure?.summary || ""
              }
              data={profile?.competitive_pressure?.competitors || []}
            />
          </div>
        </>
      ) : (
        <>
          <ProfileInfo
            data={profile}
            type="challenges"
            title={"Key Challenges & Needs"}
            description={"Understanding the roadblocks to sales growth"}
            secondHeading={"Recent Investments or Procurements Made"}
          />
          <ProfileInfo
            data={profile}
            type="priority"
            title={"Key Priorities"}
            description={`Where ${profile?.company_name} is focusing its resources`}
            secondHeading={"Revenue Report Analysis"}
          />
          {profile?.our_solutions?.length > 0 && (
            <Solutions solutions={profile?.our_solutions} />
          )}
        </>
      )}
    </div>
  );
};

export default AccountProfile;
